import { AccessTime, CallSplit, Close, SupervisedUserCircleOutlined, DeviceThermostat } from '@mui/icons-material';

import { ABTestType, CampaignChannels, SplitActions, SplitTypes } from 'domains/campaigns/types';
import IconButton from 'domains/core/components/IconButton';
import theme from 'theme';

import { CustomCampaignSteps } from '../../CustomCampaign';
import { CampaignSend, CampaignStateTypes } from '../../customCampaignReducer';
import { isSplitBranchRootSend } from '../../utils';
import CustomCampaignNavigationButton from '../CustomCampaignNavigationButton';
import CustomCampaignNavigationSend from '../CustomCampaignNavigationSend';

import useAudienceData from './useAudienceData';

const sendBlockStyles = {
    alignItems: 'center',
    display: 'flex',
};

type Props = {
    activeSendIndex: number;
    activeStep: CustomCampaignSteps;
    campaignState: CampaignStateTypes;
    index: number;
    onRemoveClick: (index: number) => void;
    setActiveSendIndex: (index: number) => void;
    setActiveStep: (step: CustomCampaignSteps) => void;
    send: CampaignSend;
};

const CustomCampaignNavigationBlock = ({
    activeSendIndex,
    activeStep,
    campaignState,
    index,
    onRemoveClick,
    send,
    setActiveSendIndex,
    setActiveStep,
}: Props) => {
    const { currentAudience } = useAudienceData({ segmentId: send.segmentId, cohortId: send.cohortId });

    const sendIsEmail = send.channel === CampaignChannels.EMAIL;
    const isABTestingContent = campaignState.abTestConfig?.testOn === ABTestType.CONTENT;
    const isABTestingSendTime = campaignState.abTestConfig?.testOn === ABTestType.SEND_TIME;

    // splitNodeId-aware so nested split branch roots (whose splitType repeats the parent
    // branch's type) are still recognised — the old parentEffectiveSplitType check missed them.
    const isBranchRoot = sendIsEmail && isSplitBranchRootSend(send, campaignState.sends);
    const shouldShowAudienceButton = index === 0 && (send.segmentId || send.cohortId);
    const shouldShowABTestButton =
        sendIsEmail && campaignState.abTestConfig && (!(isABTestingContent || isABTestingSendTime) || index === 0);
    const shouldShowTimeDelayButton =
        sendIsEmail &&
        index > 0 &&
        (!send.splitType || send.splitType === SplitTypes.MEETS || (!isBranchRoot && (send as any).parentIndex > 0)) &&
        !(isABTestingContent || isABTestingSendTime);
    const shouldShowConditionalSplitButton =
        sendIsEmail &&
        isBranchRoot &&
        send.splitType === SplitTypes.MEETS &&
        !(isABTestingContent || isABTestingSendTime);

    const isLastStep = index === campaignState.sends.length - 1;
    // TODO: update removing to account for conditional split
    const shouldShowRemoveButton =
        index > 0 && isLastStep && sendIsEmail && !send.splitType && !campaignState.abTestConfig;

    // Determine if this block's send is currently being edited
    const isActiveSendBlock = index === activeSendIndex;

    // True once this send has anything worth highlighting — its own button/chooser, or one of the
    // audience/A-B-test/time-delay/split buttons above. A freshly-created send the user hasn't
    // reached the content step for yet renders nothing in CustomCampaignNavigationSend, so without
    // this check the wrapper below would show an empty yellow-highlighted box.
    const sendHasRenderableContent = (() => {
        const anySend = send as any;
        if (!sendIsEmail) return !!anySend.content?.id || !!anySend.content?.name;
        if (anySend.splitAction === SplitActions.EXIT) return true;
        if (isBranchRoot && !anySend.splitAction) return true; // awaiting meets/exit path-action choice
        // Nested splits (ADD_SPLIT_NODE) pre-set splitAction=SEND but have no template yet at the
        // SELECT_PATH_ACTION step. The nav renders the branch-type label + email button for these;
        // sendHasRenderableContent must agree so the yellow active wrapper appears.
        if (isBranchRoot && anySend.splitAction === SplitActions.SEND && !anySend.templateId) return true;
        return !!anySend.templateId;
    })();
    const hasVisibleContent =
        shouldShowAudienceButton ||
        shouldShowABTestButton ||
        shouldShowTimeDelayButton ||
        shouldShowConditionalSplitButton ||
        sendHasRenderableContent;
    const showActiveStyling = isActiveSendBlock && hasVisibleContent;

    const getTimeDelayValue = () => {
        const { parentAudienceDelayInterval } = sendIsEmail && send;
        if (!parentAudienceDelayInterval) return;

        const delayValue = Number(parentAudienceDelayInterval?.match(/\d+/)[0]);
        return `${parentAudienceDelayInterval}${delayValue > 1 ? 's' : ''}`;
    };

    const timeDelayValue = getTimeDelayValue();

    // Apply active styling only once there's something in this block worth highlighting
    const sendWrapperStyles = {
        border: showActiveStyling
            ? `2px solid ${theme.palette.warning.main}`
            : shouldShowRemoveButton
            ? `1px dashed ${theme.palette.primary.main}`
            : 'none',
        borderRadius: '8px',
        marginRight: '8px',
        width: '90%',
        backgroundColor: showActiveStyling ? '#FFF6E9' : 'transparent',
        paddingLeft: showActiveStyling ? '8px' : '0px',
    };

    return (
        <div style={sendBlockStyles}>
            <div style={sendWrapperStyles} data-testid="send-block-wrapper">
                {shouldShowAudienceButton && (
                    <CustomCampaignNavigationButton
                        isActive={activeStep === CustomCampaignSteps.SELECT_AUDIENCE}
                        icon={<SupervisedUserCircleOutlined />}
                        label="audience"
                        onClick={() => {
                            setActiveStep(CustomCampaignSteps.SELECT_AUDIENCE);
                            // Go back to the first send so that "continue" works as expected
                            setActiveSendIndex(0);
                        }}
                        value={currentAudience?.name}
                    />
                )}
                {shouldShowABTestButton && (
                    <CustomCampaignNavigationButton
                        isActive={activeStep === CustomCampaignSteps.SETUP_A_B_TEST && index === activeSendIndex}
                        icon={<DeviceThermostat />}
                        label="A/B test"
                        onClick={() => {
                            setActiveSendIndex(index);
                            setActiveStep(CustomCampaignSteps.SETUP_A_B_TEST);
                        }}
                        value={campaignState.abTestConfig?.testOn || ''}
                    />
                )}
                {shouldShowTimeDelayButton && (
                    <CustomCampaignNavigationButton
                        isActive={activeStep === CustomCampaignSteps.SET_TIME_DELAY && index === activeSendIndex}
                        icon={<AccessTime />}
                        label="time delay"
                        onClick={() => {
                            setActiveSendIndex(index);
                            setActiveStep(CustomCampaignSteps.SET_TIME_DELAY);
                        }}
                        value={timeDelayValue}
                    />
                )}
                {shouldShowConditionalSplitButton && (
                    <CustomCampaignNavigationButton
                        isActive={activeStep === CustomCampaignSteps.CONDITIONAL_SPLIT && index === activeSendIndex}
                        icon={<CallSplit />}
                        label="conditional split"
                        onClick={() => {
                            setActiveSendIndex(index);
                            setActiveStep(CustomCampaignSteps.CONDITIONAL_SPLIT);
                        }}
                    />
                )}
                {/* A/B Testing: Render both Email-A and Email-B send buttons only from first block */}
                {(isABTestingContent || isABTestingSendTime) && index === 0 ? (
                    <>
                        {/* Email-A Send Button */}
                        <CustomCampaignNavigationSend
                            activeSendIndex={activeSendIndex}
                            activeStep={activeStep}
                            campaignState={campaignState}
                            index={0}
                            send={campaignState.sends[0]}
                            setActiveSendIndex={setActiveSendIndex}
                            setActiveStep={setActiveStep}
                        />
                        {/* Email-B Send Button */}
                        {campaignState.sends[1] && (
                            <CustomCampaignNavigationSend
                                activeSendIndex={activeSendIndex}
                                activeStep={activeStep}
                                campaignState={campaignState}
                                index={1}
                                send={campaignState.sends[1]}
                                setActiveSendIndex={setActiveSendIndex}
                                setActiveStep={setActiveStep}
                            />
                        )}
                    </>
                ) : (
                    /* Normal flow: single send navigation (skip Email-B block in A/B testing) */
                    !(isABTestingContent || isABTestingSendTime) && (
                        <CustomCampaignNavigationSend
                            activeSendIndex={activeSendIndex}
                            activeStep={activeStep}
                            campaignState={campaignState}
                            index={index}
                            send={send}
                            setActiveSendIndex={setActiveSendIndex}
                            setActiveStep={setActiveStep}
                        />
                    )
                )}
            </div>
            {shouldShowRemoveButton && (
                <IconButton handleClick={() => onRemoveClick(index)} Icon={Close} title="remove" />
            )}
        </div>
    );
};

export default CustomCampaignNavigationBlock;
